// src/components/RegisterM.jsx
import React, { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../utils/axiosInstance";
import { motion, AnimatePresence } from "framer-motion";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap-icons/font/bootstrap-icons.css";
import ImicoSignaturePad from "./ImicoSignaturePad";

// Official government theme — Deep Navy #003580 | Gold #c8a84b | White
// (matches the palette already used across the app's Akagari/login pages)
const cyberTheme = {
  pageBg: "linear-gradient(135deg, #001f4d 0%, #003580 45%, #0a4a9e 75%, #1a56b0 100%)",
  card: "rgba(255, 255, 255, 0.98)",
  text: "#1a2332",
  muted: "#718096",
  primary: "#003580",
  primaryLight: "#1a56b0",
  accent: "#c8a84b",
  accentLight: "#d4b96a",
  cardShadow: "0 24px 70px rgba(0, 20, 55, 0.35), 0 0 0 1px rgba(200, 168, 75, 0.18)",
  inputBg: "#f8fafc",
  inputBorder: "#dde3ea",
  buttonGradient: "linear-gradient(135deg, #003580, #1a56b0)",
  labelColor: "#1a2332",
};

// ===================== VALIDATION HELPERS =====================
const Validators = {
  nida: {
    clean: (value) => value.replace(/\s/g, '').replace(/\D/g, ''),
    format: (value) => {
      const cleaned = Validators.nida.clean(value).slice(0, 16);
      let formatted = '';
      for (let i = 0; i < cleaned.length; i++) {
        if (i === 1 || i === 5 || i === 6 || i === 13 || i === 14) formatted += ' ';
        formatted += cleaned[i];
      }
      return formatted;
    },
    validate: (value) => {
      if (!value || value.trim() === '') return { valid: false, error: null, empty: true };
      const cleaned = Validators.nida.clean(value);
      if (!/^\d+$/.test(cleaned) && cleaned.length > 0) return { valid: false, error: 'Indangamuntu igomba kuba imibare gusa' };
      if (cleaned.length > 0 && cleaned.length < 3) return { valid: false, error: null };
      if (cleaned.length >= 3 && !cleaned.startsWith('119') && !cleaned.startsWith('120')) return { valid: false, error: 'Indangamuntu igomba gutangira na 119 cyangwa 120' };
      if (cleaned.length > 0 && cleaned.length !== 16) return { valid: false, error: `Indangamuntu igomba kuba imibare 16 (ubu: ${cleaned.length}/16)` };
      if (cleaned.length === 16) return { valid: true, error: null };
      return { valid: false, error: null };
    },
    getPrefix: (value) => {
      if (!value) return null;
      const cleaned = Validators.nida.clean(value);
      if (cleaned.length < 3) return null;
      if (cleaned.startsWith('119')) return { prefix: '119', color: '#1a7a4a', label: 'NIDA 119', icon: 'bi-shield-check' };
      if (cleaned.startsWith('120')) return { prefix: '120', color: '#003580', label: 'NIDA 120', icon: 'bi-shield-check' };
      return { prefix: cleaned.substring(0, 3), color: '#b91c1c', label: 'Ntiyemewe', icon: 'bi-shield-x' };
    },
    getCount: (value) => {
      const cleaned = Validators.nida.clean(value);
      if (cleaned.length === 0) return null;
      return { current: cleaned.length, total: 16, isComplete: cleaned.length === 16, percentage: Math.min((cleaned.length / 16) * 100, 100) };
    },
  },
  phone: {
    clean: (value) => value.replace(/\s/g, '').replace(/\D/g, ''),
    format: (value) => {
      const cleaned = Validators.phone.clean(value).slice(0, 10);
      if (cleaned.length <= 4) return cleaned;
      if (cleaned.length <= 7) return `${cleaned.slice(0, 4)} ${cleaned.slice(4)}`;
      return `${cleaned.slice(0, 4)} ${cleaned.slice(4, 7)} ${cleaned.slice(7)}`;
    },
    validate: (value) => {
      if (!value || value.trim() === '') return { valid: false, error: null, empty: true };
      const cleaned = Validators.phone.clean(value);
      if (!/^\d+$/.test(cleaned) && cleaned.length > 0) return { valid: false, error: 'Nimero ya telefone igomba kuba imibare gusa' };
      if (cleaned.length > 0 && cleaned.length < 3) return { valid: false, error: null };
      if (cleaned.length >= 2 && !cleaned.startsWith('07')) return { valid: false, error: 'Nimero ya telefone igomba gutangira na 07' };
      if (cleaned.length >= 3) {
        const prefix3 = cleaned.substring(0, 3);
        if (!['078', '079', '072', '073'].includes(prefix3)) return { valid: false, error: 'Nimero igomba gutangira na 078/079 (MTN) cyangwa 072/073 (Airtel)' };
      }
      if (cleaned.length > 3 && cleaned.length !== 10) return { valid: false, error: `Nimero ya telefone igomba kuba imibare 10 (ubu: ${cleaned.length}/10)` };
      if (cleaned.length === 10) {
        const prefix3 = cleaned.substring(0, 3);
        if (['078', '079', '072', '073'].includes(prefix3)) return { valid: true, error: null };
        return { valid: false, error: 'Nimero igomba gutangira na 078/079 (MTN) cyangwa 072/073 (Airtel)' };
      }
      return { valid: false, error: null };
    },
    getCarrier: (value) => {
      if (!value) return null;
      const cleaned = Validators.phone.clean(value);
      if (cleaned.length < 3) return null;
      const prefix3 = cleaned.substring(0, 3);
      if (prefix3 === '078' || prefix3 === '079') return { carrier: 'MTN', color: '#fbbf24', bgColor: 'rgba(251, 191, 36, 0.12)', borderColor: 'rgba(251, 191, 36, 0.3)', icon: 'bi-phone', logo: '📶' };
      if (prefix3 === '072' || prefix3 === '073') return { carrier: 'Airtel', color: '#ef4444', bgColor: 'rgba(239, 68, 68, 0.12)', borderColor: 'rgba(239, 68, 68, 0.3)', icon: 'bi-phone', logo: '📡' };
      return null;
    },
    getCount: (value) => {
      const cleaned = Validators.phone.clean(value);
      if (cleaned.length === 0) return null;
      return { current: cleaned.length, total: 10, isComplete: cleaned.length === 10, percentage: Math.min((cleaned.length / 10) * 100, 100) };
    },
  },
};

const RegisterStyles = () => (
  <style>{`
    body {
      background: ${cyberTheme.pageBg};
      background-attachment: fixed;
      color: #f1f5f9;
      font-family: 'Nunito Sans', system-ui, -apple-system, sans-serif;
      margin: 0;
      overflow-x: hidden;
      overflow-y: auto;
      -webkit-font-smoothing: antialiased;
      -moz-osx-font-smoothing: grayscale;
    }

    *, *::before, *::after {
      box-sizing: border-box;
      -webkit-tap-highlight-color: transparent;
    }

    html {
      -webkit-text-size-adjust: 100%;
      scroll-behavior: smooth;
    }

    ::-webkit-scrollbar { width: 8px; }
    ::-webkit-scrollbar-track { background: ${cyberTheme.primary}; }
    ::-webkit-scrollbar-thumb {
      background: ${cyberTheme.accent};
      border-radius: 10px;
      border: 2px solid ${cyberTheme.primary};
    }

    .cyber-bg-glow {
      position: fixed;
      inset: 0;
      background:
        radial-gradient(rgba(200, 168, 75, 0.05) 1px, transparent 1px),
        radial-gradient(circle at 12% 15%, rgba(200,168,75,0.10) 0%, transparent 55%),
        radial-gradient(circle at 88% 85%, rgba(255,255,255,0.06) 0%, transparent 60%);
      background-size: 46px 46px, auto, auto;
      pointer-events: none;
      z-index: -2;
    }

    .cyber-container {
      display: flex;
      justify-content: center;
      align-items: center;
      min-height: 100vh;
      min-height: 100dvh;
      width: 100%;
      padding: 40px 20px;
    }

    @media (max-width: 575px) {
      .cyber-container {
        padding: 20px 12px;
        align-items: flex-start;
        padding-top: 24px;
      }
    }

    @supports (padding: env(safe-area-inset-top)) {
      .cyber-container {
        padding-top: calc(20px + env(safe-area-inset-top));
        padding-bottom: calc(20px + env(safe-area-inset-bottom));
        padding-left: calc(12px + env(safe-area-inset-left));
        padding-right: calc(12px + env(safe-area-inset-right));
      }
    }

    .cyber-card {
      background: ${cyberTheme.card};
      border-radius: 22px;
      box-shadow: ${cyberTheme.cardShadow};
      padding: 3rem;
      max-width: 700px;
      width: 100%;
      position: relative;
      overflow: hidden;
      z-index: 1;
    }

    .cyber-card::before {
      content: '';
      position: absolute;
      top: 0; left: 0; right: 0;
      height: 5px;
      background: linear-gradient(90deg, ${cyberTheme.primary}, ${cyberTheme.accent}, ${cyberTheme.primary});
    }

    @media (max-width: 575px) {
      .cyber-card {
        padding: 1.75rem 1.25rem;
        border-radius: 18px;
        box-shadow: 0 12px 40px rgba(0, 20, 55, 0.3), 0 0 0 1px rgba(200, 168, 75, 0.15);
      }
    }

    @media (min-width: 576px) and (max-width: 991px) {
      .cyber-card {
        padding: 2.5rem 2rem;
        max-width: 620px;
      }
    }

    .form-grid {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 1.5rem;
    }

    @media (max-width: 768px) {
      .form-grid { grid-template-columns: 1fr; gap: 0; }
    }

    .cyber-seal {
      width: 60px;
      height: 60px;
      border-radius: 50%;
      background: linear-gradient(135deg, ${cyberTheme.primary}, ${cyberTheme.primaryLight});
      display: flex;
      align-items: center;
      justify-content: center;
      margin: 0 auto 1.1rem;
      box-shadow: 0 4px 16px rgba(0, 53, 128, 0.3), 0 0 0 4px rgba(200, 168, 75, 0.18);
      color: #fff;
      font-size: 1.5rem;
    }

    .cyber-title {
      font-size: clamp(1.9rem, 3vw + 1rem, 2.6rem);
      font-weight: 800;
      color: ${cyberTheme.primary};
      letter-spacing: -0.02em;
      margin-bottom: 0.5rem;
      text-align: center;
      line-height: 1.15;
      word-break: break-word;
    }

    .cyber-title-underline {
      width: 60px;
      height: 4px;
      border-radius: 4px;
      background: linear-gradient(90deg, ${cyberTheme.primary}, ${cyberTheme.accent});
      margin: 0 auto clamp(1.5rem, 3vw, 2.25rem);
    }

    .input-cyber {
      background: ${cyberTheme.inputBg};
      border: 1.5px solid ${cyberTheme.inputBorder};
      border-radius: 12px;
      color: ${cyberTheme.text};
      padding: 0.8rem 2.75rem 0.8rem 3rem;
      font-size: 1rem;
      transition: all 0.25s ease;
      -webkit-appearance: none;
      -moz-appearance: none;
      touch-action: manipulation;
      /* Without this, browsers with OS/browser dark mode enabled
         auto-darken native form controls and render typed text in a
         near-invisible light gray meant for a dark input, even though
         this input is explicitly light-themed. */
      color-scheme: light;
      outline: none;
      width: 100%;
      height: 52px;
    }

    @media (max-width: 575px) {
      .input-cyber {
        font-size: 16px !important;
        padding: 0.75rem 2.5rem 0.75rem 2.6rem;
        height: 50px;
        border-radius: 10px;
      }
    }

    @supports (-webkit-touch-callout: none) {
      .input-cyber {
        font-size: 16px !important;
      }
    }

    .input-cyber:focus {
      border-color: ${cyberTheme.primary};
      box-shadow: 0 0 0 3px rgba(0,53,128,0.12);
      background: #ffffff;
      color: ${cyberTheme.text};
    }

    .input-cyber.input-error {
      border-color: #b91c1c !important;
      box-shadow: 0 0 0 3px rgba(185,28,28,0.12) !important;
    }

    .input-cyber.input-valid {
      border-color: #1a7a4a !important;
      box-shadow: 0 0 0 2px rgba(26,122,74,0.12) !important;
    }

    .input-cyber.input-mono {
      font-family: 'JetBrains Mono', 'Fira Code', 'Courier New', monospace;
      letter-spacing: 0.08em;
    }

    .input-cyber::placeholder {
      color: #9aa5b1;
    }

    .input-wrap {
      position: relative;
      height: 52px;
    }

    @media (max-width: 575px) {
      .input-wrap { height: 50px; }
    }

    .reg-field-icon {
      position: absolute;
      left: 0;
      top: 0;
      bottom: 0;
      width: 3rem;
      display: flex;
      align-items: center;
      justify-content: center;
      color: ${cyberTheme.muted};
      font-size: 1.15rem;
      pointer-events: none;
      transition: color 0.3s ease;
      z-index: 2;
    }

    @media (max-width: 575px) {
      .reg-field-icon {
        width: 2.6rem;
        font-size: 1.05rem;
      }
    }

    .reg-field-icon.icon-error { color: #b91c1c; }
    .reg-field-icon.icon-valid { color: #1a7a4a; }

    .label-cyber {
      color: ${cyberTheme.labelColor};
      font-size: 0.9rem;
      font-weight: 500;
      margin-bottom: 0.4rem;
      margin-left: 0.2rem;
      display: flex;
      align-items: center;
      gap: 6px;
      flex-wrap: wrap;
    }

    @media (max-width: 575px) {
      .label-cyber { font-size: 0.82rem; }
    }

    .btn-cyber {
      background: ${cyberTheme.buttonGradient};
      border: none;
      color: white;
      font-weight: 800;
      font-size: 1.1rem;
      padding: 1rem;
      border-radius: 12px;
      box-shadow: 0 8px 24px rgba(0,53,128,0.32);
      margin-top: 1rem;
      touch-action: manipulation;
      cursor: pointer;
      transition: all 0.3s ease;
      min-height: 54px;
    }

    @media (max-width: 575px) {
      .btn-cyber { font-size: 1rem; padding: 0.9rem; border-radius: 10px; }
    }

    .btn-cyber:hover:not(:disabled) {
      box-shadow: 0 12px 32px rgba(0,53,128,0.42);
      transform: translateY(-2px);
    }

    .btn-cyber:active:not(:disabled) { transform: translateY(0); }

    .btn-cyber:disabled {
      opacity: 0.5;
      cursor: not-allowed;
      transform: none !important;
    }

    .error-cyber {
      color: #b91c1c;
      font-size: 0.8rem;
      margin-top: 0.3rem;
    }

    .validation-progress {
      height: 3px;
      background: rgba(0,53,128,0.08);
      border-radius: 10px;
      margin-top: 6px;
      overflow: hidden;
    }

    .validation-progress-fill {
      height: 100%;
      border-radius: 10px;
      transition: all 0.3s ease;
    }

    .validation-badge {
      display: inline-flex;
      align-items: center;
      gap: 4px;
      padding: 2px 8px;
      border-radius: 8px;
      font-size: 0.65rem;
      font-weight: 700;
      letter-spacing: 0.04em;
      animation: badgePop 0.25s cubic-bezier(0.16, 1, 0.3, 1);
    }

    .validation-count {
      display: inline-flex;
      align-items: center;
      gap: 3px;
      padding: 1px 7px;
      border-radius: 6px;
      font-size: 0.62rem;
      font-weight: 700;
      font-family: 'JetBrains Mono', monospace;
      letter-spacing: 0.05em;
      margin-left: auto;
    }

    .validation-error-box {
      margin-top: 6px;
      padding: 8px 12px;
      border-radius: 10px;
      font-size: 0.75rem;
      font-weight: 600;
      display: flex;
      align-items: center;
      gap: 6px;
      animation: slideErrorIn 0.3s cubic-bezier(0.16, 1, 0.3, 1);
    }

    .validation-success-box {
      margin-top: 6px;
      padding: 8px 12px;
      border-radius: 10px;
      font-size: 0.75rem;
      font-weight: 600;
      display: flex;
      align-items: center;
      gap: 6px;
      animation: slideErrorIn 0.3s cubic-bezier(0.16, 1, 0.3, 1);
      background: rgba(26,122,74,0.08);
      border: 1px solid rgba(26,122,74,0.22);
      color: #14532d;
    }

    .carrier-badge {
      display: inline-flex;
      align-items: center;
      gap: 5px;
      padding: 3px 10px;
      border-radius: 10px;
      font-size: 0.68rem;
      font-weight: 700;
      letter-spacing: 0.03em;
      animation: badgePop 0.3s cubic-bezier(0.16, 1, 0.3, 1);
    }

    .input-status-right {
      position: absolute;
      right: 1rem;
      top: 50%;
      transform: translateY(-50%);
      font-size: 1rem;
      z-index: 3;
      pointer-events: none;
      transition: all 0.3s ease;
    }

    @media (max-width: 575px) {
      .input-status-right { right: 0.8rem; font-size: 0.9rem; }
    }

    .select-wrap .input-cyber {
      padding-right: 2.75rem;
      cursor: pointer;
    }

    .select-chevron {
      position: absolute;
      right: 1rem;
      top: 50%;
      transform: translateY(-50%);
      color: ${cyberTheme.muted};
      font-size: 0.85rem;
      pointer-events: none;
      z-index: 2;
    }

    @media (max-width: 575px) {
      .select-chevron { right: 0.8rem; font-size: 0.8rem; }
    }

    .helper-hint {
      margin-top: 6px;
      padding: 7px 11px;
      border-radius: 9px;
      font-size: 0.72rem;
      font-weight: 500;
      display: flex;
      align-items: center;
      gap: 6px;
      background: rgba(0,53,128,0.04);
      border: 1px solid rgba(0,53,128,0.1);
      color: #64748b;
      animation: badgePop 0.25s ease;
    }

    @keyframes badgePop {
      from { opacity: 0; transform: scale(0.8); }
      to { opacity: 1; transform: scale(1); }
    }

    @keyframes slideErrorIn {
      from { opacity: 0; transform: translateY(-4px); }
      to { opacity: 1; transform: translateY(0); }
    }

    @keyframes shakeInput {
      0%, 100% { transform: translateX(0); }
      15%, 55%, 85% { transform: translateX(-3px); }
      35%, 75% { transform: translateX(3px); }
    }

    .shake-anim { animation: shakeInput 0.35s ease; }

    /* ─── Message Alert Styles ─── */
    .cyber-alert {
      border-radius: 14px;
      padding: 1rem 1.5rem;
      margin-bottom: 1.5rem;
      display: flex;
      align-items: center;
      gap: 0.75rem;
      font-size: 0.95rem;
      font-weight: 500;
      backdrop-filter: blur(10px);
      -webkit-backdrop-filter: blur(10px);
      position: relative;
      overflow: hidden;
    }

    @media (max-width: 575px) {
      .cyber-alert { padding: 0.85rem 1rem; font-size: 0.85rem; border-radius: 12px; }
    }

    .cyber-alert::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      width: 4px;
      height: 100%;
    }

    .cyber-alert-success {
      background: rgba(26, 122, 74, 0.08);
      border: 1px solid rgba(26, 122, 74, 0.3);
      color: #14532d;
    }

    .cyber-alert-success::before {
      background: #1a7a4a;
    }

    .cyber-alert-danger {
      background: rgba(185, 28, 28, 0.07);
      border: 1px solid rgba(185, 28, 28, 0.3);
      color: #7f1d1d;
    }

    .cyber-alert-danger::before {
      background: #b91c1c;
    }

    .cyber-alert-warning {
      background: rgba(180, 83, 9, 0.08);
      border: 1px solid rgba(180, 83, 9, 0.3);
      color: #7c2d12;
    }

    .cyber-alert-warning::before {
      background: #b45309;
    }

    .cyber-alert-icon {
      font-size: 1.4rem;
      flex-shrink: 0;
    }

    @media (max-width: 575px) {
      .cyber-alert-icon { font-size: 1.2rem; }
    }

    .cyber-alert-close {
      margin-left: auto;
      background: none;
      border: none;
      color: inherit;
      font-size: 1.2rem;
      cursor: pointer;
      opacity: 0.6;
      transition: opacity 0.2s;
      padding: 0;
      touch-action: manipulation;
      flex-shrink: 0;
    }

    .cyber-alert-close:hover { opacity: 1; }

    /* ─── Success Registration Card ─── */
    .register-success-card {
      background: linear-gradient(135deg, rgba(26,122,74,0.06) 0%, rgba(0,53,128,0.04) 100%);
      border: 1px solid rgba(26,122,74,0.25);
      border-radius: 18px;
      padding: 2.5rem 2rem;
      text-align: center;
      margin-bottom: 1.5rem;
      position: relative;
      overflow: hidden;
    }

    .register-success-card::before {
      content: '';
      position: absolute;
      top: 0;
      left: 50%;
      transform: translateX(-50%);
      width: 80%;
      height: 2px;
      background: linear-gradient(90deg, transparent, #1a7a4a, #003580, transparent);
    }

    @media (max-width: 575px) {
      .register-success-card {
        padding: 2rem 1.25rem;
        border-radius: 16px;
      }
    }

    .success-check-circle {
      width: 80px;
      height: 80px;
      border-radius: 50%;
      background: linear-gradient(135deg, rgba(26,122,74,0.14), rgba(0,53,128,0.08));
      border: 2px solid rgba(26,122,74,0.35);
      display: flex;
      align-items: center;
      justify-content: center;
      margin: 0 auto 1.25rem;
    }

    @media (max-width: 575px) {
      .success-check-circle {
        width: 64px;
        height: 64px;
      }
    }

    .success-name {
      font-size: 1.3rem;
      font-weight: 700;
      color: ${cyberTheme.text};
      margin-bottom: 0.5rem;
    }

    @media (max-width: 575px) {
      .success-name { font-size: 1.1rem; }
    }

    .success-detail-row {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 0.5rem;
      padding: 0.4rem 0;
      font-size: 0.85rem;
      color: ${cyberTheme.muted};
    }

    .success-detail-row i {
      font-size: 0.8rem;
      color: #1a7a4a;
    }

    .success-detail-row strong {
      color: ${cyberTheme.text};
    }

    .success-badge {
      display: inline-flex;
      align-items: center;
      gap: 0.4rem;
      padding: 0.35rem 1rem;
      border-radius: 20px;
      font-size: 0.75rem;
      font-weight: 700;
      background: rgba(26,122,74,0.1);
      border: 1px solid rgba(26,122,74,0.3);
      color: #14532d;
      margin-top: 1rem;
    }

    .success-dismiss-btn {
      margin-top: 1.25rem;
      background: rgba(0,53,128,0.06);
      border: 1px solid rgba(0,53,128,0.22);
      color: ${cyberTheme.primary};
      font-size: 0.85rem;
      font-weight: 600;
      padding: 0.6rem 1.5rem;
      border-radius: 10px;
      cursor: pointer;
      transition: all 0.3s ease;
      touch-action: manipulation;
    }

    .success-dismiss-btn:hover {
      background: rgba(0,53,128,0.12);
      border-color: rgba(0,53,128,0.4);
    }

    /* ─── ADDED: Enhanced Success Overlay Modal ─── */
    .success-fullscreen-overlay {
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: rgba(0, 15, 38, 0.72);
      backdrop-filter: blur(10px);
      -webkit-backdrop-filter: blur(10px);
      display: flex;
      justify-content: center;
      align-items: center;
      z-index: 999999;
      padding: 1.5rem;
    }

    .success-modal-container {
      background: #ffffff;
      border-radius: 26px;
      padding: 3rem 2.5rem;
      max-width: 540px;
      width: 100%;
      text-align: center;
      box-shadow: 0 30px 90px rgba(0, 15, 38, 0.45), 0 0 0 1px rgba(200, 168, 75, 0.2);
      position: relative;
      overflow: hidden;
    }

    @media (max-width: 575px) {
      .success-modal-container {
        padding: 2.2rem 1.5rem;
        border-radius: 20px;
        margin: 0 8px;
      }
    }

    .success-modal-container::after {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      height: 5px;
      background: linear-gradient(90deg, #1a7a4a, #003580, #c8a84b);
    }

    .success-modal-icon-wrap {
      width: 92px;
      height: 92px;
      border-radius: 50%;
      background: linear-gradient(135deg, #1a7a4a, #14532d);
      display: flex;
      align-items: center;
      justify-content: center;
      margin: 0 auto 1.5rem;
      box-shadow: 0 8px 28px rgba(26, 122, 74, 0.35);
      position: relative;
      z-index: 2;
    }

    @media (max-width: 575px) {
      .success-modal-icon-wrap {
        width: 76px;
        height: 76px;
      }
    }

    .success-modal-icon-wrap i {
      font-size: 2.4rem;
      color: white;
    }

    @media (max-width: 575px) {
      .success-modal-icon-wrap i {
        font-size: 1.9rem;
      }
    }

    .success-modal-ring-pulse {
      animation: modalRingPulse 2s ease-in-out infinite;
    }

    @keyframes modalRingPulse {
      0%, 100% { box-shadow: 0 0 0 0 rgba(26,122,74,0.35), 0 8px 28px rgba(26,122,74,0.35); }
      50% { box-shadow: 0 0 0 14px rgba(26,122,74,0), 0 8px 28px rgba(26,122,74,0.35); }
    }

    .success-modal-heading {
      font-size: 2rem;
      font-weight: 800;
      color: #14532d;
      margin-bottom: 0.5rem;
      position: relative;
      z-index: 2;
    }

    @media (max-width: 575px) {
      .success-modal-heading {
        font-size: 1.6rem;
      }
    }

    .success-modal-subtitle {
      font-size: 0.88rem;
      color: ${cyberTheme.muted};
      font-weight: 600;
      margin-bottom: 1.25rem;
      position: relative;
      z-index: 2;
    }

    .success-modal-user-name {
      font-size: 1.5rem;
      font-weight: 800;
      color: ${cyberTheme.text};
      margin-bottom: 0.3rem;
      position: relative;
      z-index: 2;
    }

    @media (max-width: 575px) {
      .success-modal-user-name {
        font-size: 1.25rem;
      }
    }

    .success-modal-role-badge {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      padding: 5px 16px;
      border-radius: 20px;
      font-size: 0.78rem;
      font-weight: 700;
      background: rgba(0, 53, 128, 0.07);
      border: 1px solid rgba(0, 53, 128, 0.22);
      color: ${cyberTheme.primary};
      margin-bottom: 1.25rem;
      position: relative;
      z-index: 2;
    }

    .success-modal-details-grid {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 10px;
      margin-bottom: 1.5rem;
      position: relative;
      z-index: 2;
    }

    @media (max-width: 420px) {
      .success-modal-details-grid {
        grid-template-columns: 1fr;
        gap: 8px;
      }
    }

    .success-modal-detail-item {
      background: #f8fafc;
      border: 1px solid #e2e8f0;
      border-radius: 12px;
      padding: 0.7rem 0.9rem;
      text-align: left;
      transition: all 0.2s ease;
    }

    .success-modal-detail-item:hover {
      background: rgba(0,53,128,0.04);
      border-color: rgba(0,53,128,0.15);
    }

    .success-modal-detail-label {
      font-size: 0.65rem;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.06em;
      color: #64748b;
      margin-bottom: 3px;
      display: flex;
      align-items: center;
      gap: 4px;
    }

    .success-modal-detail-label i {
      font-size: 0.6rem;
      color: #1a7a4a;
    }

    .success-modal-detail-value {
      font-size: 0.85rem;
      font-weight: 600;
      color: ${cyberTheme.text};
      word-break: break-all;
    }

    .success-modal-timestamp {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      padding: 4px 14px;
      border-radius: 16px;
      font-size: 0.7rem;
      font-weight: 700;
      background: rgba(26,122,74,0.08);
      border: 1px solid rgba(26,122,74,0.22);
      color: #14532d;
      margin-bottom: 1.5rem;
      position: relative;
      z-index: 2;
    }

    .success-modal-actions {
      display: flex;
      gap: 12px;
      position: relative;
      z-index: 2;
    }

    @media (max-width: 420px) {
      .success-modal-actions {
        flex-direction: column;
      }
    }

    .success-modal-btn-primary {
      flex: 1;
      background: linear-gradient(90deg, #1a7a4a, #14532d);
      border: none;
      color: white;
      font-weight: 700;
      font-size: 0.95rem;
      height: 52px;
      border-radius: 14px;
      box-shadow: 0 8px 24px rgba(26, 122, 74, 0.3);
      transition: all 0.3s ease;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 8px;
      touch-action: manipulation;
    }

    .success-modal-btn-primary:hover {
      transform: translateY(-2px);
      box-shadow: 0 12px 32px rgba(26, 122, 74, 0.4);
    }

    .success-modal-btn-secondary {
      flex: 1;
      background: #f8fafc;
      border: 1px solid #dde3ea;
      color: ${cyberTheme.muted};
      font-weight: 600;
      font-size: 0.9rem;
      height: 52px;
      border-radius: 14px;
      transition: all 0.3s ease;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 8px;
      touch-action: manipulation;
    }

    .success-modal-btn-secondary:hover {
      background: #eef2f6;
      border-color: #c9d2db;
      color: ${cyberTheme.text};
    }

    .success-modal-particle {
      position: absolute;
      border-radius: 50%;
      opacity: 0;
      z-index: 0;
      pointer-events: none;
      animation: modalParticleFloat 3s ease-in-out infinite;
    }

    .success-modal-particle:nth-child(1) {
      width: 6px; height: 6px; background: #1a7a4a;
      top: 10%; left: 8%; animation-delay: 0s;
    }
    .success-modal-particle:nth-child(2) {
      width: 4px; height: 4px; background: #c8a84b;
      top: 18%; right: 10%; animation-delay: 0.7s;
    }
    .success-modal-particle:nth-child(3) {
      width: 5px; height: 5px; background: #003580;
      bottom: 15%; left: 12%; animation-delay: 1.4s;
    }
    .success-modal-particle:nth-child(4) {
      width: 3px; height: 3px; background: #1a7a4a;
      bottom: 22%; right: 8%; animation-delay: 2.1s;
    }
    .success-modal-particle:nth-child(5) {
      width: 4px; height: 4px; background: #c8a84b;
      top: 45%; left: 5%; animation-delay: 0.4s;
    }
    .success-modal-particle:nth-child(6) {
      width: 5px; height: 5px; background: #003580;
      top: 55%; right: 6%; animation-delay: 1.1s;
    }

    @keyframes modalParticleFloat {
      0%, 100% { opacity: 0; transform: scale(0) translateY(0); }
      30% { opacity: 0.8; transform: scale(1.2) translateY(-6px); }
      70% { opacity: 0.6; transform: scale(0.9) translateY(-12px); }
    }

    .success-modal-divider {
      height: 1px;
      background: linear-gradient(90deg, transparent, rgba(0,53,128,0.15), rgba(200,168,75,0.2), transparent);
      margin: 1rem 0 1.25rem;
      position: relative;
      z-index: 2;
    }

    @media (prefers-reduced-motion: reduce) {
      *, *::before, *::after {
        animation-duration: 0.01ms !important;
        transition-duration: 0.01ms !important;
      }
    }

    /* Signature pad (shared look with ImicoSignaturePad) */
    .imico-btn { display:inline-flex; align-items:center; gap:6px; padding:10px 18px; border:none; border-radius:7px; font-size:13px; font-weight:700; cursor:pointer; font-family:inherit; }
    .imico-btn:disabled { opacity:.5; cursor:not-allowed; }
    .imico-btn-primary { background:${cyberTheme.primary}; color:#fff; }
    .imico-btn-outline { background:transparent; color:#475569; border:1px solid ${cyberTheme.inputBorder}; }
    .imico-btn-outline:hover { background:#f1f5f9; }
    .imico-btn-sm { padding:7px 13px; font-size:12px; }
    .imico-sig-pad-wrap { margin-top:10px; }
    .imico-sig-pad-meta { display:flex; justify-content:space-between; font-size:12px; color:#475569; margin-bottom:6px; }
    .imico-sig-pad-label { font-weight:700; }
    .imico-sig-pad { background:#f8fafc; border:2px dashed #cbd5e1; border-radius:8px; padding:10px; }
    .imico-sig-canvas { width:100% !important; height:160px !important; border-radius:6px; background:#fff; border:1px solid #e2e8f0; touch-action:none; }
    .imico-sig-actions { display:flex; gap:10px; margin-top:8px; }
    .imico-alert { padding:10px 14px; border-radius:6px; font-size:12px; margin-bottom:10px; }
    .imico-alert-warning { background:#fef3c7; border:1px solid #fde68a; color:#b45309; }
  `}</style>
);

const RegisterM = ({ onSuccess }) => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    izina_ry_isibo: "",
    national_id: "", fullname: "", email: "", telefone: "",
    password: "", confirmPassword: "", role: ""
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [touched, setTouched] = useState({});
  const [message, setMessage] = useState({ show: false, text: "", type: "" });
  // Signature captured at registration time — auto-stamped onto this
  // Isibo/Umunyamabanga leader's future reports via GET /signature/my-signature.
  const [signatureImage, setSignatureImage] = useState(null);

  // Track the registered user details for the success card
  const [registeredUser, setRegisteredUser] = useState(null);

  // ADDED: Track whether to show the full-screen success modal
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  const showMessage = (text, type = "success") => {
    setMessage({ show: true, text, type });
    if (type === "success") {
      setTimeout(() => setMessage({ show: false, text: "", type: "" }), 8000);
    }
  };

  const closeMessage = () => {
    setMessage({ show: false, text: "", type: "" });
  };

  // Dismiss success card
  const dismissSuccessCard = () => {
    setRegisteredUser(null);
    setShowSuccessModal(false);
  };

  // ADDED: Close modal and register another
  const handleRegisterAnother = () => {
    setShowSuccessModal(false);
    setRegisteredUser(null);
  };

  // ADDED: Close modal and navigate away / trigger onSuccess
  const handleSuccessDone = () => {
    setShowSuccessModal(false);
    setRegisteredUser(null);
    if (onSuccess) {
      onSuccess();
    }
  };

  const getMessageIcon = (type) => {
    switch (type) {
      case "success": return "bi-check-circle-fill";
      case "danger": return "bi-x-circle-fill";
      case "warning": return "bi-exclamation-triangle-fill";
      default: return "bi-info-circle-fill";
    }
  };

  const nidaValidation = Validators.nida.validate(formData.national_id);
  const nidaPrefix = Validators.nida.getPrefix(formData.national_id);
  const nidaCount = Validators.nida.getCount(formData.national_id);
  const phoneValidation = Validators.phone.validate(formData.telefone);
  const phoneCarrier = Validators.phone.getCarrier(formData.telefone);
  const phoneCount = Validators.phone.getCount(formData.telefone);

  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    let processedValue = value;
    if (name === 'national_id') processedValue = Validators.nida.format(value);
    if (name === 'telefone') processedValue = Validators.phone.format(value);
    setFormData((prev) => ({ ...prev, [name]: processedValue }));
    if (message.show && message.type !== "success") closeMessage();
  }, [message.show, message.type]);

  const handleBlur = useCallback((fieldName) => {
    setTouched((prev) => ({ ...prev, [fieldName]: true }));
  }, []);

  const getInputClass = (fieldName) => {
    let cls = 'input-cyber';
    if (fieldName === 'national_id') {
      cls += ' input-mono';
      if (touched.national_id && nidaValidation?.error) cls += ' input-error';
      else if (nidaValidation?.valid) cls += ' input-valid';
    }
    if (fieldName === 'telefone') {
      if (touched.telefone && phoneValidation?.error) cls += ' input-error';
      else if (phoneValidation?.valid) cls += ' input-valid';
    }
    return cls;
  };

  const getIconWrapClass = (fieldName) => {
    let cls = 'reg-field-icon';
    if (fieldName === 'national_id') {
      if (touched.national_id && nidaValidation?.error) cls += ' icon-error';
      else if (nidaValidation?.valid) cls += ' icon-valid';
    }
    if (fieldName === 'telefone') {
      if (touched.telefone && phoneValidation?.error) cls += ' icon-error';
      else if (phoneValidation?.valid) cls += ' icon-valid';
    }
    return cls;
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setTouched({
      national_id: true, fullname: true, email: true,
      telefone: true, role: true, password: true, confirmPassword: true,
    });

    if (!formData.national_id.trim()) { showMessage("Injiza indangamuntu yawe.", "warning"); if (navigator.vibrate) navigator.vibrate([100, 50, 100]); return; }
    const nidaCheck = Validators.nida.validate(formData.national_id);
    if (!nidaCheck.valid) { showMessage(nidaCheck.error || "Indangamuntu ntiyujuje ibisabwa.", "warning"); if (navigator.vibrate) navigator.vibrate([100, 50, 100]); return; }
    if (!formData.fullname.trim()) { showMessage("Injiza amazina yawe yose.", "warning"); return; }
    if (!formData.email.trim()) { showMessage("Injiza imeyili yawe.", "warning"); return; }
    if (!formData.telefone.trim()) { showMessage("Injiza numero ya telefone.", "warning"); return; }
    const phoneCheck = Validators.phone.validate(formData.telefone);
    if (!phoneCheck.valid) { showMessage(phoneCheck.error || "Nimero ya telefone ntiyujuje ibisabwa.", "warning"); if (navigator.vibrate) navigator.vibrate([100, 50, 100]); return; }
    if (!formData.role) { showMessage("Hitamo inshingano yawe.", "warning"); return; }
    if (!formData.password) { showMessage("Injiza ijambo ry'ibanga.", "warning"); return; }
    if (formData.password.length < 6) { showMessage("Ijambo ry'ibanga rigomba kuba nibura inyuguti 6.", "warning"); return; }
    if (formData.password !== formData.confirmPassword) { showMessage("Amagambo y'ibanga ntahura. Ongera ugerageze.", "danger"); if (navigator.vibrate) navigator.vibrate([100, 50, 100]); return; }
    if (!signatureImage) { showMessage("Shyiraho umukono wawe mbere yo kwemeza.", "warning"); return; }

    setIsLoading(true);
    try {
      const submitData = {
        ...formData,
        national_id: Validators.nida.clean(formData.national_id),
        telefone: Validators.phone.clean(formData.telefone),
        signature_image: signatureImage,
      };

      // Decode sup_id from stored JWT token
      const token = localStorage.getItem("token");
      let sup_id = null;
      if (token) {
        try {
          const payload = JSON.parse(atob(token.split(".")[1]));
          sup_id = payload.id;
        } catch (_) {}
      }
      if (!sup_id) {
        showMessage("Ntabwo winjiye. Banza winjire (Login).", "danger");
        setIsLoading(false);
        return;
      }

      await axiosInstance.post("/register_abandi", { ...submitData, sup_id });

      if (navigator.vibrate) navigator.vibrate(50);

      // Save registered user details for the success card
      const savedUser = {
        fullname: formData.fullname,
        email: formData.email,
        telefone: Validators.phone.format(formData.telefone),
        national_id: Validators.nida.format(formData.national_id),
        role: formData.role,
        timestamp: new Date().toLocaleTimeString('rw-RW', { hour: '2-digit', minute: '2-digit' }),
        date: new Date().toLocaleDateString('rw-RW', { day: '2-digit', month: 'long', year: 'numeric' }),
      };

      setRegisteredUser(savedUser);

      // ADDED: Show the full-screen success modal
      setShowSuccessModal(true);

      showMessage(
        `${formData.fullname} yiyandikishijwe neza! Ubu arashobora kwinjira mu buryo.`,
        "success"
      );

      setFormData({
        izina_ry_isibo: "",
        national_id: "", fullname: "", email: "", telefone: "",
        password: "", confirmPassword: "", role: ""
      });
      setSignatureImage(null);
      setTouched({});

      if (onSuccess) {
        setTimeout(() => onSuccess(), 3000);
      }
    } catch (err) {
      const errorMsg = err.response?.data?.error || err.response?.data?.message || "Habaye ikosa mu kwiyandikisha. Ongera ugerageze.";
      showMessage(`${errorMsg}`, "danger");
      if (navigator.vibrate) navigator.vibrate([100, 50, 100]);
    } finally {
      setIsLoading(false);
    }
  };

  const renderNidaFeedback = () => {
    const showError = touched.national_id && nidaValidation?.error;
    const showValid = nidaValidation?.valid;
    const isActive = formData.national_id.length > 0;
    return (
      <>
        {isActive && nidaCount && (
          <div className="validation-progress">
            <div className="validation-progress-fill" style={{
              width: `${nidaCount.percentage}%`,
              background: nidaCount.isComplete ? 'linear-gradient(90deg, #1a7a4a, #003580)' : showError ? 'linear-gradient(90deg, #b91c1c, #b45309)' : 'linear-gradient(90deg, #003580, #c8a84b)',
            }} />
          </div>
        )}
        {showError && (
          <div className="validation-error-box" style={{ background: 'rgba(185,28,28,0.06)', border: '1px solid rgba(185,28,28,0.2)', color: '#7f1d1d' }}>
            <i className="bi bi-exclamation-triangle-fill" style={{ fontSize: '0.7rem' }} />{nidaValidation.error}
          </div>
        )}
        {showValid && (
          <div className="validation-success-box">
            <i className="bi bi-shield-fill-check" style={{ fontSize: '0.75rem' }} />Indangamuntu yujujwe neza ({nidaPrefix?.label})
          </div>
        )}
        {!isActive && touched.national_id === undefined && (
          <div className="helper-hint">
            <i className="bi bi-info-circle" style={{ fontSize: '0.68rem', color: '#003580' }} />
            Tangira na <strong style={{ color: '#1a7a4a' }}>119</strong> cyangwa <strong style={{ color: '#003580' }}>120</strong> — Imibare 16
          </div>
        )}
      </>
    );
  };

  const renderPhoneFeedback = () => {
    const showError = touched.telefone && phoneValidation?.error;
    const showValid = phoneValidation?.valid;
    const isActive = formData.telefone.length > 0;
    return (
      <>
        {isActive && phoneCount && (
          <div className="validation-progress">
            <div className="validation-progress-fill" style={{
              width: `${phoneCount.percentage}%`,
              background: phoneCount.isComplete ? 'linear-gradient(90deg, #1a7a4a, #003580)' : showError ? 'linear-gradient(90deg, #b91c1c, #b45309)' : 'linear-gradient(90deg, #003580, #c8a84b)',
            }} />
          </div>
        )}
        {phoneCarrier && (
          <div className="carrier-badge" style={{ background: phoneCarrier.bgColor, border: `1px solid ${phoneCarrier.borderColor}`, color: phoneCarrier.color, marginTop: '6px' }}>
            <span>{phoneCarrier.logo}</span>
            <i className={`bi ${phoneCarrier.icon}`} style={{ fontSize: '0.6rem' }} />{phoneCarrier.carrier}
            {phoneValidation?.valid && <i className="bi bi-check-circle-fill" style={{ fontSize: '0.6rem', marginLeft: '2px' }} />}
          </div>
        )}
        {showError && (
          <div className="validation-error-box" style={{ background: 'rgba(185,28,28,0.06)', border: '1px solid rgba(185,28,28,0.2)', color: '#7f1d1d' }}>
            <i className="bi bi-exclamation-triangle-fill" style={{ fontSize: '0.7rem' }} />{phoneValidation.error}
          </div>
        )}
        {showValid && !phoneCarrier && (
          <div className="validation-success-box">
            <i className="bi bi-telephone-fill" style={{ fontSize: '0.7rem' }} />Nimero ya telefone yujujwe neza
          </div>
        )}
        {!isActive && (
          <div className="helper-hint">
            <i className="bi bi-info-circle" style={{ fontSize: '0.68rem', color: '#003580' }} />
            MTN: <strong style={{ color: '#fbbf24' }}>078/079</strong> — Airtel: <strong style={{ color: '#ef4444' }}>072/073</strong> — Imibare 10
          </div>
        )}
      </>
    );
  };

  return (
    <>
      <RegisterStyles />
      <div className="cyber-bg-glow" />

      {/* ADDED: Full-screen Success Modal Overlay */}
      <AnimatePresence>
        {showSuccessModal && registeredUser && (
          <motion.div
            className="success-fullscreen-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35 }}
            onClick={handleSuccessDone}
          >
            <motion.div
              className="success-modal-container"
              initial={{ opacity: 0, scale: 0.6, y: 60 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.6, y: 60 }}
              transition={{ type: "spring", stiffness: 260, damping: 22, delay: 0.1 }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Floating particles */}
              <div className="success-modal-particle" />
              <div className="success-modal-particle" />
              <div className="success-modal-particle" />
              <div className="success-modal-particle" />
              <div className="success-modal-particle" />
              <div className="success-modal-particle" />

              {/* Animated check icon */}
              <motion.div
                className="success-modal-icon-wrap success-modal-ring-pulse"
                initial={{ scale: 0, rotate: -180 }}
                animate={{ scale: 1, rotate: 0 }}
                transition={{ type: "spring", stiffness: 200, damping: 13, delay: 0.35 }}
              >
                <motion.i
                  className="bi bi-check-lg"
                  initial={{ opacity: 0, scale: 0 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.55, type: "spring", stiffness: 300 }}
                />
              </motion.div>

              {/* Heading */}
              <motion.h2
                className="success-modal-heading"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
              >
                Byagenze Neza!
              </motion.h2>

              {/* Subtitle */}
              <motion.p
                className="success-modal-subtitle"
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6 }}
              >
                Umuyobozi bozi wakagari yiyandikishijwe neza muri sisitemu
              </motion.p>

              {/* User name */}
              <motion.div
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.65 }}
              >
                <div className="success-modal-user-name">{registeredUser.fullname}</div>
              </motion.div>

              {/* Role badge */}
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.7, type: "spring", stiffness: 200 }}
              >
                <div className="success-modal-role-badge">
                  <i className="bi bi-briefcase-fill" style={{ fontSize: '0.7rem' }} />
                  {registeredUser.role}
                </div>
              </motion.div>

              {/* Divider */}
              <div className="success-modal-divider" />

              {/* Details grid */}
              <motion.div
                className="success-modal-details-grid"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.75 }}
              >
                <div className="success-modal-detail-item">
                  <div className="success-modal-detail-label">
                    <i className="bi bi-envelope-fill" /> Imeyili
                  </div>
                  <div className="success-modal-detail-value">{registeredUser.email}</div>
                </div>
                <div className="success-modal-detail-item">
                  <div className="success-modal-detail-label">
                    <i className="bi bi-telephone-fill" /> Telefone
                  </div>
                  <div className="success-modal-detail-value">{registeredUser.telefone}</div>
                </div>
                <div className="success-modal-detail-item">
                  <div className="success-modal-detail-label">
                    <i className="bi bi-fingerprint" /> Indangamuntu
                  </div>
                  <div className="success-modal-detail-value" style={{ fontFamily: "'JetBrains Mono', monospace", letterSpacing: '0.05em' }}>
                    {registeredUser.national_id}
                  </div>
                </div>
                <div className="success-modal-detail-item">
                  <div className="success-modal-detail-label">
                    <i className="bi bi-calendar-event-fill" /> Itariki
                  </div>
                  <div className="success-modal-detail-value">{registeredUser.date}</div>
                </div>
              </motion.div>

              {/* Timestamp badge */}
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.85 }}
                style={{ position: 'relative', zIndex: 2 }}
              >
                <div className="success-modal-timestamp">
                  <i className="bi bi-clock-fill" style={{ fontSize: '0.6rem' }} />
                  Yanditswe: {registeredUser.timestamp}
                </div>
              </motion.div>

              {/* Action buttons */}
              <motion.div
                className="success-modal-actions"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.9 }}
              >
                <motion.button
                  className="success-modal-btn-primary"
                  onClick={handleRegisterAnother}
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.97 }}
                >
                  <i className="bi bi-plus-circle-fill" />
                  Andikisha Undi
                </motion.button>
                <motion.button
                  className="success-modal-btn-secondary"
                  onClick={handleSuccessDone}
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.97 }}
                >
                  <i className="bi bi-check2-all" />
                  Soza
                </motion.button>
              </motion.div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="cyber-container">
        <motion.div
          className="cyber-card"
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
        >
          <div className="cyber-seal">
            <i className="bi bi-person-badge-fill" />
          </div>
          <h1 className="cyber-title">kubarura</h1>
          <div className="cyber-title-underline" />

          {/* ─── Message Display ─── */}
          <AnimatePresence>
            {message.show && (
              <motion.div
                className={`cyber-alert cyber-alert-${message.type}`}
                initial={{ opacity: 0, y: -20, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: -20, scale: 0.95 }}
                transition={{ duration: 0.35, ease: "easeOut" }}
              >
                <i className={`bi ${getMessageIcon(message.type)} cyber-alert-icon`}></i>
                <span>{message.text}</span>
                <button className="cyber-alert-close" onClick={closeMessage} type="button">
                  <i className="bi bi-x-lg"></i>
                </button>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Success Registration Card — shows after successful register (inline version) */}
          <AnimatePresence>
            {registeredUser && !showSuccessModal && (
              <motion.div
                className="register-success-card"
                initial={{ opacity: 0, y: -30, scale: 0.9 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: -20, scale: 0.95 }}
                transition={{ duration: 0.5, ease: [0.23, 1, 0.32, 1] }}
              >
                {/* Animated check icon */}
                <motion.div
                  className="success-check-circle"
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.2, type: "spring", stiffness: 200, damping: 12 }}
                >
                  <motion.i
                    className="bi bi-check-lg"
                    style={{ fontSize: '2.2rem', color: '#10b981' }}
                    initial={{ opacity: 0, scale: 0 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.4, type: "spring", stiffness: 300 }}
                  />
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                >
                  <p style={{ fontSize: '0.82rem', color: '#6ee7b7', marginBottom: '0.75rem', fontWeight: 600 }}>
                    Kwiyandikisha byagenze neza!
                  </p>

                  <div className="success-name">{registeredUser.fullname}</div>

                  <div style={{ marginTop: '0.75rem' }}>
                    <div className="success-detail-row">
                      <i className="bi bi-envelope-fill" />
                      <strong>{registeredUser.email}</strong>
                    </div>
                    <div className="success-detail-row">
                      <i className="bi bi-telephone-fill" />
                      <strong>{registeredUser.telefone}</strong>
                    </div>
                    <div className="success-detail-row">
                      <i className="bi bi-fingerprint" />
                      <strong>{registeredUser.national_id}</strong>
                    </div>
                    <div className="success-detail-row">
                      <i className="bi bi-briefcase-fill" />
                      <strong>{registeredUser.role}</strong>
                    </div>
                  </div>

                  <div className="success-badge">
                    <i className="bi bi-clock" style={{ fontSize: '0.65rem' }} />
                    Yanditswe: {registeredUser.timestamp}
                  </div>

                  <div>
                    <button
                      type="button"
                      className="success-dismiss-btn"
                      onClick={dismissSuccessCard}
                    >
                      <i className="bi bi-plus-circle me-2" style={{ fontSize: '0.8rem' }} />
                      Andikisha undi
                    </button>
                  </div>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>

          <form onSubmit={handleRegister}>
            <div className="form-grid">
              {/* National ID */}
              <div className="position-relative mb-3">
                <label className="label-cyber">
                  Indangamuntu (16)
                  {nidaPrefix && (
                    <span className="validation-badge" style={{ background: `${nidaPrefix.color}18`, color: nidaPrefix.color, border: `1px solid ${nidaPrefix.color}30` }}>
                      <i className={`bi ${nidaPrefix.icon}`} style={{ fontSize: '0.55rem' }} />{nidaPrefix.label}
                    </span>
                  )}
                  {nidaCount && (
                    <span className="validation-count" style={{
                      background: nidaCount.isComplete ? 'rgba(26,122,74,0.12)' : 'rgba(0,53,128,0.06)',
                      color: nidaCount.isComplete ? '#1a7a4a' : '#718096',
                      border: `1px solid ${nidaCount.isComplete ? 'rgba(26,122,74,0.25)' : 'rgba(0,53,128,0.15)'}`,
                    }}>
                      {nidaCount.current}/{nidaCount.total}
                    </span>
                  )}
                </label>
                <div className="input-wrap">
                  <span className={getIconWrapClass('national_id')}><i className="bi bi-fingerprint" /></span>
                  <input name="national_id" className={getInputClass('national_id')} placeholder="1 1990 8 0000000 0 00" value={formData.national_id} onChange={handleChange} onBlur={() => handleBlur('national_id')} maxLength={22} inputMode="numeric" autoComplete="off" />
                  {touched.national_id && nidaValidation?.error && <i className="bi bi-exclamation-circle-fill input-status-right" style={{ color: '#ef4444' }} />}
                  {nidaValidation?.valid && <i className="bi bi-check-circle-fill input-status-right" style={{ color: '#10b981' }} />}
                </div>
                {renderNidaFeedback()}
              </div>

              {/* Isibo name */}
              <div className="position-relative mb-3">
                <label className="label-cyber">Izina ry'Isibo</label>
                <div className="input-wrap">
                  <span className="reg-field-icon"><i className="bi bi-geo-alt"></i></span>
                  <input name="izina_ry_isibo" className="input-cyber" placeholder="Izina ry'isibo..." value={formData.izina_ry_isibo} onChange={handleChange} />
                </div>
              </div>

              {/* Fullname */}
              <div className="position-relative mb-3">
                <label className="label-cyber">Amazina Yose</label>
                <div className="input-wrap">
                  <span className="reg-field-icon"><i className="bi bi-person"></i></span>
                  <input name="fullname" className="input-cyber" placeholder="Amazina yose" value={formData.fullname} onChange={handleChange} autoComplete="name" />
                </div>
              </div>

              {/* Email */}
              <div className="position-relative mb-3">
                <label className="label-cyber">Imeyili (Email)</label>
                <div className="input-wrap">
                  <span className="reg-field-icon"><i className="bi bi-envelope"></i></span>
                  <input name="email" type="email" className="input-cyber" placeholder="Email" value={formData.email} onChange={handleChange} autoComplete="email" />
                </div>
              </div>

              {/* Phone */}
              <div className="position-relative mb-3">
                <label className="label-cyber">
                  Telefone (10)
                  {phoneCarrier && (
                    <span className="validation-badge" style={{ background: phoneCarrier.bgColor, color: phoneCarrier.color, border: `1px solid ${phoneCarrier.borderColor}` }}>
                      <span style={{ fontSize: '0.6rem' }}>{phoneCarrier.logo}</span>{phoneCarrier.carrier}
                    </span>
                  )}
                  {phoneCount && (
                    <span className="validation-count" style={{
                      background: phoneCount.isComplete ? 'rgba(26,122,74,0.12)' : 'rgba(0,53,128,0.06)',
                      color: phoneCount.isComplete ? '#1a7a4a' : '#718096',
                      border: `1px solid ${phoneCount.isComplete ? 'rgba(26,122,74,0.25)' : 'rgba(0,53,128,0.15)'}`,
                    }}>
                      {phoneCount.current}/{phoneCount.total}
                    </span>
                  )}
                </label>
                <div className="input-wrap">
                  <span className={getIconWrapClass('telefone')}><i className="bi bi-telephone" /></span>
                  <input name="telefone" className={getInputClass('telefone')} placeholder="078X XXX XXX" value={formData.telefone} onChange={handleChange} onBlur={() => handleBlur('telefone')} maxLength={12} inputMode="tel" autoComplete="tel" />
                  {touched.telefone && phoneValidation?.error && <i className="bi bi-exclamation-circle-fill input-status-right" style={{ color: '#ef4444' }} />}
                  {phoneValidation?.valid && <i className="bi bi-check-circle-fill input-status-right" style={{ color: '#10b981' }} />}
                </div>
                {renderPhoneFeedback()}
              </div>
            </div>

            {/* Role */}
            <div className="position-relative mb-3">
              <label className="label-cyber">Inshingano</label>
              <div className="input-wrap select-wrap">
                <span className="reg-field-icon"><i className="bi bi-briefcase"></i></span>
                <select name="role" className="input-cyber" value={formData.role} onChange={handleChange} style={{ appearance: 'none' }}>
                  <option value="">-- Hitamo Inshingano --</option>
                  <option value="Isibo">Umuyobozi w'Isibo</option>
                  <option value="Umunyamabanga">miberehomyiza</option>
                </select>
                <i className="bi bi-chevron-down select-chevron"></i>
              </div>
            </div>

            {/* Passwords */}
            <div className="form-grid">
              <div className="position-relative mb-3">
                <label className="label-cyber">Ijambo ry'ibanga</label>
                <div className="input-wrap">
                  <span className="reg-field-icon"><i className="bi bi-lock"></i></span>
                  <input name="password" type={showPassword ? "text" : "password"} className="input-cyber" placeholder="Nibura inyuguti 6..." value={formData.password} onChange={handleChange} autoComplete="new-password" />
                </div>
              </div>
              <div className="position-relative mb-3">
                <label className="label-cyber">Emeza Ijambo ry'ibanga</label>
                <div className="input-wrap">
                  <span className="reg-field-icon"><i className="bi bi-shield-check"></i></span>
                  <input name="confirmPassword" type={showPassword ? "text" : "password"} className="input-cyber" placeholder="Ongera urisubiremo..." value={formData.confirmPassword} onChange={handleChange} autoComplete="new-password" />
                </div>
              </div>
            </div>

            {/* Show password toggle */}
            <div className="mb-4 d-flex align-items-center">
              <input type="checkbox" id="showPass" checked={showPassword} onChange={() => setShowPassword(!showPassword)} style={{ accentColor: cyberTheme.primary, width: '18px', height: '18px', cursor: 'pointer' }} />
              <label htmlFor="showPass" className="ms-2 small" style={{ cursor: 'pointer', color: cyberTheme.text }}>Erekana ijambo ry'ibanga</label>
            </div>

            {/* Signature */}
            <div className="mb-4">
              <label className="label-cyber">Umukono w'Umuyobozi</label>
              {signatureImage ? (
                <div className="d-flex align-items-center gap-3 flex-wrap">
                  <img src={signatureImage} alt="Umukono" style={{ height: 70, border: `1px solid ${cyberTheme.inputBorder}`, borderRadius: 8, background: "#fff" }} />
                  <button type="button" className="imico-btn imico-btn-outline imico-btn-sm" onClick={() => setSignatureImage(null)}>
                    Hindura Umukono
                  </button>
                </div>
              ) : (
                <ImicoSignaturePad
                  label="Shyiraho umukono wawe"
                  signerName={formData.fullname || "Umuyobozi mushya"}
                  signerRole={formData.role}
                  onSign={(dataUrl) => setSignatureImage(dataUrl)}
                />
              )}
            </div>

            {/* Submit */}
            <motion.button type="submit" className="btn-cyber w-100" whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }} disabled={isLoading}>
              {isLoading ? (
                <><span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>Tegereza...</>
              ) : (
                <><i className="bi bi-person-plus me-2"></i>EMEZA KWIYANDIKISHA</>
              )}
            </motion.button>
          </form>
        </motion.div>
      </div>
    </>
  );
};

export default RegisterM;